import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useDispatch } from "react-redux";
import { Play } from "../../../compoment/icon";
import {
  getActiveAiSelfieArtsByCategory,
  type AiSelfieArt,
} from "../../../services/aiSelfieArtService";
import {
  is90sCartoonFlow,
  isCaricatureHobbyAiSelfieArt,
  isCaricaturePortraitFlow,
  persistPortraitSubject,
} from "../../../services/stylePromptResolver";
import { resolveArtDisplayName } from "../../../utils/artDisplayName";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import {
  enrichStationWithThemeBackgrounds,
  isFifaWorldCup2026Station,
  persistSelectedAiSelfieArt,
  persistStationSelection,
  preloadFifaWorldCupSelectedArt,
  readActiveStoredStation,
  readSelectedAiSelfieArtFromLocalStorage,
  readSelectedStation,
  readStoredStationData,
  resetKioskLandingAppState,
  resolveStationCategorySlug,
  resolveStationRouteSlug,
  syncIdleStartNowLocalStorage,
} from "../../../utils/stationFlowStorage";

function formatStationTitle(name: string): ReactNode {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  return (
    <>
      {parts[0]}
      <br />
      {parts.slice(1).join(" ")}
    </>
  );
}

function slugifyArtName(name?: string | null): string {
  return (name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const AllInnerStation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: slugParam } = useParams();
  const dispatch = useDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const stationData = readStoredStationData();
  const selectedStation = readSelectedStation();
  const artFromNav = (location.state as { selectedArt?: AiSelfieArt | null } | null)
    ?.selectedArt;
  const [selectedArt, setSelectedArt] = useState<AiSelfieArt | null>(
    () => artFromNav || readSelectedAiSelfieArtFromLocalStorage()
  );
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  // Same route component is reused across /:slug/idle — resync art when slug/art changes.
  useEffect(() => {
    const fromStorage = readSelectedAiSelfieArtFromLocalStorage();
    const slug = slugParam?.trim() || "";

    const matchesSlug = (art?: AiSelfieArt | null) =>
      Boolean(art && slug && slugifyArtName(art.name) === slug);

    const nextArt =
      (matchesSlug(artFromNav) ? artFromNav : null) ||
      (matchesSlug(fromStorage) ? fromStorage : null) ||
      artFromNav ||
      fromStorage ||
      null;

    if (!nextArt) return;

    persistSelectedAiSelfieArt(nextArt);
    setSelectedArt((prev) => {
      if (
        prev?.id != null &&
        nextArt.id != null &&
        String(prev.id) === String(nextArt.id) &&
        prev.home_bg_image === nextArt.home_bg_image &&
        prev.inner_bg_image === nextArt.inner_bg_image
      ) {
        return prev;
      }
      return nextArt;
    });

    const activeStation =
      readSelectedStation() || readStoredStationData() || readActiveStoredStation();
    if (activeStation) {
      persistStationSelection(
        enrichStationWithThemeBackgrounds(activeStation, nextArt)
      );
    }
  }, [artFromNav, location.key, slugParam]);

  const displayName = resolveArtDisplayName({ selectedArt, stationData });

  const title = useMemo(() => formatStationTitle(displayName), [displayName]);
  const isFifaWorldCup2026 =
    isFifaWorldCup2026Station(selectedStation) || isFifaWorldCup2026Station(stationData);
  const isCaricatureHobby =
    !isFifaWorldCup2026 && isCaricatureHobbyAiSelfieArt(selectedArt);
  const isCaricaturePortrait =
    !isFifaWorldCup2026 &&
    !isCaricatureHobby &&
    isCaricaturePortraitFlow({
      slug: slugParam,
      stationType: selectedStation?.type || stationData?.type,
      artName: selectedArt?.name,
    });
  const is90sCartoon =
    !isFifaWorldCup2026 &&
    !isCaricatureHobby &&
    !isCaricaturePortrait &&
    is90sCartoonFlow({
      slug: slugParam,
      stationType: selectedStation?.type || stationData?.type,
      artName: selectedArt?.name,
    });
  const useLargeIdleStartHitArea = (() => {
    const slug = String(slugParam || "").trim().toLowerCase();
    const type = String(selectedStation?.type || stationData?.type || "")
      .trim()
      .toLowerCase();
    const name = String(selectedArt?.name || "").trim().toLowerCase();
    const isWynwoodCaricature =
      slug === "wynwood-caricature" ||
      type === "wynwood-caricature" ||
      (name.includes("wynwood") && name.includes("caricature"));
    const isPetCaricature =
      slug === "pet-caricature" ||
      type === "pet-caricature" ||
      (name.includes("pet") && name.includes("caricature"));
    return isWynwoodCaricature || isPetCaricature;
  })();

  useEffect(() => {
    resetKioskLandingAppState(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (selectedArt?.home_bg_image?.trim() || isCaricatureHobby) return;
    videoRef.current?.play().catch(() => { });
  }, [selectedArt?.home_bg_image, isCaricatureHobby]);

  useEffect(() => {
    if (!isFifaWorldCup2026 || selectedArt || !stationData) return;

    let cancelled = false;

    (async () => {
      const art = await preloadFifaWorldCupSelectedArt(stationData);
      if (!cancelled && art) {
        setSelectedArt(art);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isFifaWorldCup2026, selectedArt, stationData]);

  const handleStartNow = () => {
    const art = selectedArt || readSelectedAiSelfieArtFromLocalStorage();
    syncIdleStartNowLocalStorage({
      selectedArt: art,
      isCaricatureHobby,
      isFifaWorldCup2026,
    });

    const optionSlug = resolveStationRouteSlug(
      selectedStation || stationData,
      slugParam,
      art
    );
    const navState = {
      stationData: selectedStation || stationData,
      selectedArt: art,
    };

    // Hobby caricature arts skip choose-option and keep the art slug in the URL.
    if (isCaricatureHobby) {
      navigate(`/${optionSlug}/select-people`, { state: navState });
      return;
    }

    // 90's cartoon: select-people-cartoon before take/upload.
    if (is90sCartoon) {
      navigate(`/${optionSlug}/select-people-cartoon`, { state: navState });
      return;
    }

    // Caricature portrait: idle → choose-option (People/Pets step removed).
    if (isCaricaturePortrait) {
      persistPortraitSubject("people");
    }

    navigate(`/${optionSlug}/choose-option`, { state: navState });
  };

  const handleBackToCategoryAll = async () => {
    if (isNavigatingBack) return;

    const activeStation =
      selectedStation || stationData || readActiveStoredStation();
    if (activeStation?.type?.trim()) {
      persistStationSelection(activeStation);
    }

    const categorySlug = resolveStationCategorySlug(activeStation, slugParam);
    const categoryId =
      activeStation?.id != null ? String(activeStation.id).trim() : "";

    setIsNavigatingBack(true);
    try {
      // Mirror home open: >1 active arts → /all, otherwise home.
      let artCount = 0;
      if (categoryId) {
        const artsRes = await getActiveAiSelfieArtsByCategory(categoryId);
        artCount = artsRes.success ? artsRes.data.length : 0;
      }

      if (artCount > 1 && categorySlug) {
        navigate(`/${categorySlug}/all`, {
          replace: true,
          state: {
            stationData: activeStation,
            selectedArt,
          },
        });
        return;
      }

      navigate("/", { replace: true });
    } catch {
      navigate("/", { replace: true });
    }
  };

  const backgroundImage = selectedArt?.home_bg_image?.trim() || "";
  const useImageStartOverlay = Boolean(backgroundImage) || isCaricatureHobby;

  return (
    <StationThemeLayout
      className={`idleSection p-0${isFifaWorldCup2026
        ? " idleSectionFifaWorldCup2026"
        : isCaricatureHobby
          ? " idleSectionCaricatureHobby"
          : backgroundImage
            ? " idleSectionArtBackground"
            : ""
        }${useLargeIdleStartHitArea ? " idleSectionWynwoodCaricature" : ""}`}
      station={selectedStation || stationData}
      showStationLogo={false}
      useThemeBackground={false}
      onBack={handleBackToCategoryAll}
      style={backgroundImage ? { backgroundImage: `url("${backgroundImage}")` } : undefined}
    >
      {!useImageStartOverlay && (
        <>
          <video
            ref={videoRef}
            className="backgroundVideo"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/bg-vedio.mp4" type="video/mp4" />
          </video>
          <div className="overlay" />
        </>
      )}

      {useImageStartOverlay ? (
        <button
          type="button"
          className="idleArtBackgroundStart"
          aria-label="Start now"
          onClick={handleStartNow}
        />
      ) : (
        <div className="idleLandingContent">
          {!isFifaWorldCup2026 ? (
            <h1 className="idleLandingTitle">
              <span className="idleLandingTitleStroke" aria-hidden="true">
                {title}
              </span>
              <span className="idleLandingTitleText">{title}</span>
            </h1>
          ) : null}
          <button type="button" className={isFifaWorldCup2026 ? "idleLandingBtn fifaBtn" : "idleLandingBtn"} onClick={handleStartNow}>
            START NOW
            <span className="ms-3">
              <Play />
            </span>
          </button>
        </div>
      )}
    </StationThemeLayout>
  );
};

export default AllInnerStation;
